import axios from 'axios';
import { API_BASE_URL } from './api';
import { clearClientAuth } from '../utils/auditSafety';
import { getSessionVersion } from './session';
import { validateRequestBody } from '../utils/requestValidation';

const base = new URL(API_BASE_URL, window.location.origin);
const basePath = base.pathname.replace(/\/$/, '');
const apiUrl = input => {
  const url = new URL(input, window.location.origin);
  return url.origin === base.origin && (url.pathname === basePath || url.pathname.startsWith(basePath + '/')) ? url : null;
};
const isAuthEndpoint = url => ['/login', '/logout', '/user/me'].some(p => url.pathname === basePath + p);
let redirecting = false;
export const handleSessionExpiration = () => {
  if (redirecting) return;
  redirecting = true;
  clearClientAuth();
  sessionStorage.setItem('loginMessage', 'Your session has expired. Please sign in again.');
  window.location.replace('/login');
};
const checkSession = (url, version, status, data) => {
  if (!url || isAuthEndpoint(url) || !version || version !== getSessionVersion()) return;
  if (status === 401 || data?.error === 'Session expired, please login again') handleSessionExpiration();
};

const originalFetch = window.fetch.bind(window);
window.fetch = async (input, options) => {
  const url = apiUrl(typeof input === 'string' ? input : input.url || String(input));
  if (!url) return originalFetch(input, options);
  const version = getSessionVersion();
  const headers = new Headers(options?.headers ?? (typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined));
  headers.delete('Authorization');
  const request = typeof Request !== 'undefined' && input instanceof Request ? input : null;
  let body = options?.body;
  const method = options?.method || request?.method || 'GET';
  if (body === undefined && request && /^(POST|PUT|PATCH|DELETE)$/i.test(method)) {
    const copy = request.clone();
    const contentType = headers.get('content-type') || '';
    body = contentType.includes('multipart/form-data') ? await copy.formData()
      : contentType.includes('application/x-www-form-urlencoded') ? new URLSearchParams(await copy.text()) : await copy.text();
    if (body === '') body = undefined;
  }
  await validateRequestBody(body, method, url.pathname);
  const response = await originalFetch(input, { ...options, headers, credentials: 'include' });
  checkSession(url, version, response.status);
  if (response.status !== 401 && response.headers.get('content-type')?.includes('application/json')) {
    try { checkSession(url, version, response.status, await response.clone().json()); } catch { /* Screens handle unreadable responses. */ }
  }
  return response;
};

// Feedback uses Axios; apply the same cookie and expiration rules.
axios.interceptors.request.use(async config => {
  const url = apiUrl(axios.getUri(config));
  if (url) {
    await validateRequestBody(config.data, config.method, url.pathname);
    config.withCredentials = true;
    config.headers = axios.AxiosHeaders.from(config.headers);
    config.headers.delete('Authorization');
    config.rcsSessionVersion = getSessionVersion();
  }
  return config;
});
axios.interceptors.response.use(response => {
  checkSession(apiUrl(axios.getUri(response.config)), response.config.rcsSessionVersion, response.status, response.data);
  return response;
}, error => {
  if (error.config) checkSession(apiUrl(axios.getUri(error.config)), error.config.rcsSessionVersion, error.response?.status, error.response?.data);
  return Promise.reject(error);
});
